import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { TagList } from '../components/TagBadge';
import { fmtDate, fmtMinutes } from '../utils/dates';
import { printReport } from '../utils/printReport';

const STATUS_AR = {
  pending: 'معلّقة', in_progress: 'قيد التنفيذ', waiting_approval: 'بانتظار الاعتماد',
  approved: 'معتمدة', revision: 'مراجعة', completed: 'مكتملة', cancelled: 'ملغاة',
};

const PROJECT_STATUS_AR = {
  active: 'نشط', paused: 'متوقف', completed: 'مكتمل', archived: 'مؤرشف',
};
const PROJECT_STATUS_COLOR = {
  active: 'in_progress', paused: 'pending', completed: 'completed', archived: 'cancelled',
};

export default function SponsorReport() {
  const { id } = useParams();
  const { api } = useAuth();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [filters, setFilters] = useState({ month: '', from: '', to: '', status: '', sheikh_id: '' });
  const [activeSheikh, setActiveSheikh] = useState('all');

  const buildQs = useCallback(() => {
    const qs = new URLSearchParams();
    if (filters.month) qs.set('month', filters.month);
    else {
      if (filters.from) qs.set('from', filters.from);
      if (filters.to) qs.set('to', filters.to);
    }
    if (filters.status) qs.set('status', filters.status);
    if (filters.sheikh_id) qs.set('sheikh_id', filters.sheikh_id);
    return qs;
  }, [filters]);

  const load = useCallback(() => {
    setLoading(true);
    api(`/reports/sponsor/${id}?${buildQs()}`)
      .then(setData)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [id, buildQs, api, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api('/reports/months').then(setMonths).catch(() => {});
  }, [api]);

  const tasks = data?.tasks || [];

  const filteredTasks = useMemo(() => {
    if (activeSheikh === 'all') return tasks;
    return tasks.filter(t => String(t.sheikh_id) === String(activeSheikh));
  }, [tasks, activeSheikh]);

  const rangeLabel = () => {
    if (filters.month) {
      const m = months.find(mm => mm.value === filters.month);
      return m ? m.label : filters.month;
    }
    if (filters.from || filters.to) return `من ${filters.from || '—'} إلى ${filters.to || '—'}`;
    return 'كامل المدة';
  };

  const printAll = () => {
    const rows = filteredTasks.map(t => ({
      title: t.title,
      sheikh: t.sheikh?.name || '—',
      project: t.project?.title || '—',
      assignee: t.assignee?.name || '—',
      status: STATUS_AR[t.status] || t.status,
      progress: progressLabel(t),
      last_completed_step: t.last_completed_step || '—',
      tags: (t.tags || []).map(x => x.name).join('، '),
      due_date: t.due_date ? fmtDate(t.due_date) : '—',
    }));
    printReport({
      title: `تقرير الداعم: ${data?.sponsor?.name || ''}`,
      subtitle: rangeLabel(),
      columns: [
        { key: 'title', label: 'المهمة' },
        { key: 'sheikh', label: 'الشيخ' },
        { key: 'project', label: 'المشروع' },
        { key: 'assignee', label: 'المنفّذ' },
        { key: 'status', label: 'الحالة' },
        { key: 'progress', label: 'الإنجاز' },
        { key: 'last_completed_step', label: 'آخر خطوة مكتملة' },
        { key: 'tags', label: 'الوسوم' },
        { key: 'due_date', label: 'الاستحقاق' },
      ],
      rows,
    });
  };

  const downloadCsv = () => {
    const cols = [
      ['title', 'المهمة'], ['sheikh', 'الشيخ'], ['project', 'المشروع'],
      ['assignee', 'المنفّذ'], ['status', 'الحالة'], ['progress', 'الإنجاز'],
      ['last_completed_step', 'آخر خطوة مكتملة'], ['tags', 'الوسوم'],
      ['due_date', 'الاستحقاق'], ['logged_minutes', 'دقائق العمل'],
    ];
    const lines = [cols.map(c => c[1]).join(',')];
    filteredTasks.forEach(t => {
      const row = {
        title: t.title,
        sheikh: t.sheikh?.name || '',
        project: t.project?.title || '',
        assignee: t.assignee?.name || '',
        status: STATUS_AR[t.status] || t.status,
        progress: progressLabel(t),
        last_completed_step: t.last_completed_step || '',
        tags: (t.tags || []).map(x => x.name).join('؛ '),
        due_date: t.due_date ? fmtDate(t.due_date) : '',
        logged_minutes: t.logged_minutes || 0,
      };
      lines.push(cols.map(c => `"${String(row[c[0]] ?? '').replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sponsor-${data?.sponsor?.name || id}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading && !data) return <div className="loader">جاري التحميل...</div>;
  if (!data) return <div className="empty">لا توجد بيانات</div>;

  return (
    <div>
      <Link to="/sponsors" style={{ fontSize: 13, color: 'var(--text-muted)', display: 'inline-block', marginBottom: 8 }}>← الداعمون</Link>
      <div className="report-cover" style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
        <div className="report-cover-inner">
          <div className="report-cover-text">
            <div className="report-cover-kicker">تقرير الداعم</div>
            <h1 className="report-cover-title">{data.sponsor.name}</h1>
            <div className="report-cover-period">{rangeLabel()}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <div>
            <label className="form-label">شهر مغلق</label>
            <select className="form-select" value={filters.month}
              onChange={e => setFilters(f => ({ ...f, month: e.target.value, from: '', to: '' }))}>
              <option value="">— مدى مخصّص —</option>
              {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">من</label>
            <input className="form-input" type="date" disabled={!!filters.month}
              value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} />
          </div>
          <div>
            <label className="form-label">إلى</label>
            <input className="form-input" type="date" disabled={!!filters.month}
              value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} />
          </div>
          <div>
            <label className="form-label">الحالة</label>
            <select className="form-select" value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
              <option value="">كل الحالات</option>
              {Object.entries(STATUS_AR).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-primary btn-sm" onClick={load}>🔄 تحديث</button>
          <button className="btn btn-outline btn-sm" onClick={printAll}>🖨 طباعة / PDF</button>
          <button className="btn btn-outline btn-sm" onClick={downloadCsv}>📥 CSV</button>
        </div>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
          <Stat label="مشاريع" value={data.totals.projects} />
          <Stat label="مشايخ" value={data.totals.sheikhs} />
          <Stat label="إجمالي المهام" value={data.totals.tasks} />
          <Stat label="مكتملة" value={data.totals.completed} color="var(--success)" />
          <Stat label="نشطة" value={data.totals.active} color="var(--primary)" />
          <Stat label="وقت العمل" value={fmtMinutes(data.totals.logged_minutes)} />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>المشاريع ({data.projects.length})</div>
        {data.projects.length === 0 ? (
          <div className="empty">لا مشاريع</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {data.projects.map(p => (
              <div key={p.id} style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 6 }}>
                <Link to={`/projects/${p.id}`} style={{ fontWeight: 600 }}>{p.title}</Link>
                <div style={{ marginTop: 6 }}>
                  <span className={`badge badge-${PROJECT_STATUS_COLOR[p.status] || 'pending'}`}>{PROJECT_STATUS_AR[p.status] || p.status}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  المشايخ ({p.sheikhs.length}): {p.sheikhs.map(s => s.name).join('، ')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>تجميع حسب الشيخ</div>
        {data.by_sheikh.length === 0 ? (
          <div className="empty">لا توجد مشايخ مرتبطة</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>الشيخ</th><th>المشاريع</th><th>إجمالي المهام</th><th>مكتملة</th><th>نشطة</th><th>التقرير</th></tr>
              </thead>
              <tbody>
                {data.by_sheikh.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 500 }}>{s.name}</td>
                    <td>{s.projects.map(p => p.title).join('، ')}</td>
                    <td>{s.tasks}</td>
                    <td><span className="badge badge-completed">{s.completed}</span></td>
                    <td><span className="badge badge-in_progress">{s.active}</span></td>
                    <td><Link className="btn btn-sm btn-outline" to={`/reports/sheikh/${s.id}`}>📊 تفاصيل</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title" style={{ marginBottom: 12 }}>المهام التفصيلية ({filteredTasks.length})</div>
        <div className="filters" style={{ marginBottom: 10 }}>
          <button className={`btn btn-sm ${activeSheikh === 'all' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setActiveSheikh('all')}>كل المشايخ ({tasks.length})</button>
          {data.by_sheikh.map(s => (
            <button key={s.id} className={`btn btn-sm ${activeSheikh === String(s.id) ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveSheikh(String(s.id))}>{s.name} ({s.tasks})</button>
          ))}
        </div>
        {filteredTasks.length === 0 ? (
          <div className="empty">لا توجد مهام</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>المهمة</th><th>الشيخ</th><th>المشروع</th><th>المنفّذ</th><th>الحالة</th><th>الإنجاز</th><th>آخر خطوة مكتملة</th><th>الوسوم</th><th>الاستحقاق</th></tr>
              </thead>
              <tbody>
                {filteredTasks.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 500 }}>{t.title}</td>
                    <td>{t.sheikh?.name || '—'}</td>
                    <td>{t.project?.title || '—'}</td>
                    <td>{t.assignee?.name || '—'}</td>
                    <td><span className={`badge badge-${t.status}`}>{STATUS_AR[t.status] || t.status}</span></td>
                    <td>{progressLabel(t)}</td>
                    <td>{t.last_completed_step || '—'}</td>
                    <td><TagList tags={t.tags || []} /></td>
                    <td>{t.due_date ? fmtDate(t.due_date) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', padding: 10 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}

function progressLabel(t) {
  return t.steps_total ? `${t.progress_percent}% (${t.steps_done}/${t.steps_total})` : `${t.progress_percent ?? 0}%`;
}
